import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Stack, Typography } from '@mui/material';

import { ProviderIcon } from '@components/content';
import ImageGroupDisplay from '@components/media/images/Images/ImageGroupDisplay';
import ImageGroupDataProvider from '@hooks/imageHooks/useImageGroupData/provider';
import { useProviderNames } from '@hooks/useProviderNames';
import { DetailedTitle } from '@models/Title';
import { getTitleImageUrlFromProvider } from '@utils/imageUtils';

type ImageTabProps = {
  title: DetailedTitle;
};

const ImageTab: FC<ImageTabProps> = ({ title, title: { masterId } }) => {
  const { t } = useTranslation();

  const uniqueProviders = Array.from(
    new Set(title?.playbackOptions.map(({ providerIdentifier }) => providerIdentifier))
  );

  const { getProviderName } = useProviderNames();

  return (
    <Stack spacing={2}>
      <Typography component='h4' gutterBottom variant='h2'>
        {t('aggregated')}
      </Typography>

      <ImageGroupDisplay />

      <Divider />

      <Box>
        <Typography component='h4' gutterBottom variant='h2'>
          {t('images.byProvider')}
        </Typography>

        <Stack spacing={2}>
          {uniqueProviders.map(providerIdentifier => {
            const providerName = getProviderName(providerIdentifier);

            return (
              <Box key={providerIdentifier}>
                <Stack alignItems='center' direction='row' mb={1} spacing={1.5}>
                  <ProviderIcon
                    providerIdentifier={providerIdentifier}
                    size='large'
                    variant='rounded'
                  />

                  <Typography component='h5' variant='h3'>
                    {providerName}
                  </Typography>
                </Stack>

                <ImageGroupDataProvider
                  filenameBase={`${title.titleName} - ${providerName}`}
                  getImageUrl={(_, imageType) =>
                    getTitleImageUrlFromProvider(title, imageType, providerIdentifier)
                  }
                  imageId={`${masterId}-${providerIdentifier}`}
                >
                  <ImageGroupDisplay />
                </ImageGroupDataProvider>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

export default ImageTab;
